import { ROTATION } from '../game/constants';
import { STRINGS } from '../ui/strings.zh-HK';
import type { GyroController } from './GyroController';
import type { InputRouter } from './InputRouter';
import type { InputState } from './InputState';

type HoldDir = 'left' | 'right';

/**
 * On-screen controls — the touch counterpart to the keyboard.
 *
 * A bottom bar (safely inset from the home indicator) with:
 *   陀螺儀   enable / disable gyro steering
 *   ◀ / ▶  hold to spin the board (the router's 'buttons' source)
 *   ↻      restart the round (the existing `resetRequested` path — no new code path)
 *
 * Priority is handled by the `InputRouter`: a drag on the board still beats both
 * the gyro and these buttons, so a player can always ignore the bar and just
 * swipe. DOM, not UI, so text stays crisp at any pixel ratio and carries
 * `aria-label`s; mounted under `#hud`, reaped on dispose.
 */
export class MobileControls {
  readonly root: HTMLDivElement;
  private readonly held = new Set<HoldDir>();
  private readonly gyroButton: HTMLButtonElement;

  constructor(
    parent: HTMLElement,
    private readonly router: InputRouter,
    private readonly input: InputState,
    private readonly gyro: GyroController,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'control-bar';

    this.gyroButton = this.buildButton('gyro', STRINGS.controls.gyro, '◎');
    this.gyroButton.addEventListener('click', () => void this.toggleGyro());

    const left = this.buildHold('left', STRINGS.controls.left, '◀');
    const right = this.buildHold('right', STRINGS.controls.right, '▶');
    const restart = this.buildButton('restart', STRINGS.controls.restart, '↻');
    restart.addEventListener('click', () => {
      this.input.resetRequested = true;
    });

    const note = document.createElement('p');
    note.className = 'control-note';
    note.textContent = STRINGS.controls.portraitHint;

    this.root.append(this.gyroButton, left, right, restart, note);
    parent.appendChild(this.root);
    this.syncGyro();
  }

  private buildButton(action: string, label: string, glyph: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'control-button';
    button.dataset.action = action;
    button.textContent = glyph;
    button.setAttribute('aria-label', label);
    return button;
  }

  private buildHold(dir: HoldDir, label: string, glyph: string): HTMLButtonElement {
    const button = this.buildButton(dir, label, glyph);
    button.addEventListener('pointerdown', (event) => this.press(dir, event));
    button.addEventListener('pointerup', () => this.release(dir));
    button.addEventListener('pointercancel', () => this.release(dir));
    button.addEventListener('pointerleave', () => this.release(dir));
    return button;
  }

  private press(dir: HoldDir, event: PointerEvent): void {
    event.preventDefault();
    this.held.add(dir);
    this.applyHold();
  }

  private release(dir: HoldDir): void {
    this.held.delete(dir);
    this.applyHold();
  }

  private applyHold(): void {
    const direction = (this.held.has('right') ? 1 : 0) - (this.held.has('left') ? 1 : 0);
    this.router.set('buttons', direction * ROTATION.maxOmega, direction !== 0);
  }

  private async toggleGyro(): Promise<void> {
    if (this.gyro.isEnabled) this.gyro.disable();
    else await this.gyro.enable();
    this.syncGyro();
  }

  private syncGyro(): void {
    if (!this.gyro.isSupported) {
      this.gyroButton.disabled = true;
      this.gyroButton.textContent = STRINGS.controls.gyroUnsupported;
    } else {
      this.gyroButton.disabled = false;
      this.gyroButton.textContent = this.gyro.isEnabled ? STRINGS.controls.gyroOn : STRINGS.controls.gyroOff;
    }
    this.gyroButton.setAttribute('aria-pressed', String(this.gyro.isEnabled));
  }

  dispose(): void {
    this.held.clear();
    this.router.set('buttons', 0, false);
    this.root.remove();
  }
}
